import { useEffect, useState } from 'react';
import { Icon } from '@/design-system/icons/Icon';
import { Button } from '@/components/atoms/Button';
import { readLS, writeLS } from '@/services/storage/local';

type BeforeInstallEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

const DISMISS_KEY = 'canapyra-install-dismissed-at';
const DISMISS_COOLDOWN_DAYS = 14;

const wasRecentlyDismissed = (): boolean => {
  const ts = readLS<number>(DISMISS_KEY, 0);
  if (!ts) return false;
  return Date.now() - ts < DISMISS_COOLDOWN_DAYS * 86_400_000;
};

export const InstallPrompt = () => {
  const [event, setEvent] = useState<BeforeInstallEvent | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (wasRecentlyDismissed()) return;

    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setEvent(e as BeforeInstallEvent);
      setVisible(true);
    };

    const onInstalled = () => setVisible(false);

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = async () => {
    if (!event) return;
    await event.prompt();
    const choice = await event.userChoice;
    if (choice.outcome === 'dismissed') writeLS(DISMISS_KEY, Date.now());
    setVisible(false);
  };

  const dismiss = () => {
    writeLS(DISMISS_KEY, Date.now());
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Instalar CanaBiz"
      style={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 8500,
        background: 'var(--surface)',
        border: '1px solid var(--line-2)',
        borderRadius: 'var(--r-3)',
        boxShadow: 'var(--sh-3)',
        padding: 18,
        maxWidth: 320,
        display: 'grid',
        gap: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ width: 38, height: 38, borderRadius: 'var(--r-2)', background: 'var(--primary)', color: 'var(--primary-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon name="download" size={18} />
        </span>
        <strong>Instalar CanaBiz</strong>
      </div>
      <p style={{ fontSize: 13, color: 'var(--ink-3)', margin: 0 }}>
        Acesso rápido, funciona offline para leitura, sem barra do navegador.
      </p>
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        <Button variant="ghost" size="sm" onClick={dismiss}>
          Agora não
        </Button>
        <Button size="sm" onClick={install}>
          Instalar
        </Button>
      </div>
    </div>
  );
};
